import { captureError } from "./sentry";

/**
 * ONE GUARD FOR THE WHOLE MAP, INSTEAD OF ONE PER CALL SITE.
 *
 * THE BUG (Sentry, first seen Jul 30, still arriving Aug 15 — 20 events, 18
 * users): `TypeError: undefined is not an object (evaluating
 * 'this.style.getOwnLayer')`, thrown from inside `map.getLayer()`, caught by
 * the React error boundary — which white-screens the page. The breadcrumbs are
 * always the same shape: a tap, a route change, a crash 40-60ms later. The
 * Aug 15 instance was someone navigating to /support, which is where people go
 * when something is ALREADY wrong.
 *
 * WHY IT HAPPENS. MapLibre sets `map.style = undefined` in `remove()`. React
 * then keeps running: sibling effect cleanups, queued `styledata` callbacks,
 * in-flight fetches that resolve a moment late. Any of them that touches a
 * layer dereferences a style that isn't there.
 *
 * WHY THE PREVIOUS FIXES DIDN'T HOLD. `mapAlive()` was added Aug 1 and then
 * applied one site at a time, over three commits — the teardown path, then
 * removeGoogle + bike lanes, then the cheap-restyle effect — each time a new
 * Sentry event named a new place. An audit on Aug 16 found all 13 `getLayer`
 * call sites guarded, and the issue still arriving. That is the tell: the
 * strategy is wrong, not the guards. There are FOUR maps in this app
 * (MapView, NavigationView, RoutePreviewOverlay, Admin) and 64 style-touching
 * calls across them; a rule enforced by remembering it at 64 places is a rule
 * that will be forgotten at the 65th.
 *
 * WHAT THIS DOES. Once, at construction, it wraps the style-touching methods
 * of ONE map instance so that after the map is dead they return an empty
 * answer instead of throwing. Every existing call site already handles the
 * empty answer — they are all written as `if (map.getLayer(id)) ...` — so
 * behaviour on a live map is bit-for-bit unchanged, and behaviour on a dead
 * map goes from "white-screen the app" to "do nothing", which is what a
 * teardown wants.
 *
 * IT IS NOT A SILENCER (rule 17). The first swallowed call per map logs a
 * one-line confession naming the method, so a real bug that happens to hide
 * behind this still leaves a trail. It only ever swallows when the map is
 * PROVABLY gone — never on a live map, where a genuine error must still
 * surface.
 *
 * The honest caveat: this patches instance methods of a third-party object,
 * which is not something to reach for casually. It earns its place because
 * the alternative — a guard at every call site, forever, in four components —
 * has now failed four times in seventeen days.
 *
 * ADDED PLE-6 (Oct 2): a direct `webglcontextlost` report. Everything above
 * only catches context loss AFTER THE FACT, and only if something else
 * happens to call a guarded method once the map goes quiet — the confession
 * at the bottom of this file has said "(or lost its WebGL context)" since
 * the THIRD CRASH below, but that line only fires on a downstream symptom.
 * A map that loses its context and is simply never touched again today
 * leaves no trace anywhere. That is the un-instrumented middle of Morgan's
 * Oct 2 theory ("something builds up over a session... then the map goes
 * white") and this file's own mapAlive() comment, which already names the
 * trigger: "iOS does this to a backgrounded WKWebView, and to a page running
 * several GL contexts at once, which this app is." See `mapAlive` below.
 */

/** Is this map still usable? `_removed` is MapLibre-internal, but it is the
 *  only honest signal that `remove()` has been called.
 *
 *  `style` goes missing in TWO situations, not one: `remove()` deletes it,
 *  and `_contextLost` (WebGL context loss — iOS does this to a backgrounded
 *  WKWebView, and to a page running several GL contexts at once, which this
 *  app is) sets it to null on a map that is otherwise fully alive, with its
 *  event handlers still bound. Both must read as "not alive" here, because
 *  every style-touching method throws the same way in both. But see the
 *  getCanvas note below: the CANVAS survives both, and pretending it doesn't
 *  was its own crash. */
export function mapAlive(map: unknown): boolean {
  if (!map) return false;
  const m = map as { style?: unknown; _removed?: boolean };
  return !!m.style && !m._removed;
}

/** Methods that reach into `map.style` and therefore throw once it's gone.
 *  Split by WHAT AN EMPTY ANSWER LOOKS LIKE to the caller — which is the whole
 *  design of this module. A guard that hands back the wrong shape of nothing
 *  doesn't remove a crash, it moves it: `queryRenderedFeatures()` returning
 *  `undefined` would turn "cannot read style" into "cannot read .filter of
 *  undefined" one line later, which is a worse bug because it no longer names
 *  the map. Every entry below returns what the live method returns when it
 *  finds nothing. */
const READ_METHODS = [
  "getLayer", "getSource", "getStyle", "getPaintProperty", "getLayoutProperty",
  "getFilter",
] as const;
/** These return arrays when they find nothing, so a dead map must too. */
const QUERY_METHODS = ["queryRenderedFeatures", "querySourceFeatures"] as const;
/**
 * CAMERA AND CANVAS — the methods the first version of this file missed.
 *
 * The guard originally covered only the style-layer methods, because the
 * Sentry stack named getLayer. But a torn-down map is torn down for
 * everything, and the app calls plenty more on it. Counted across src/:
 * getCanvas 11 times, easeTo 10, fitBounds 5, resize 5, flyTo 3, jumpTo 2,
 * setStyle 1 — none protected.
 *
 * getCanvas is the one to be careful with. Every hover handler does
 *
 *     map.getCanvas().style.cursor = "pointer";
 *
 * so the first version of this guard handed a dead map's callers a bare
 * `{ style: {} }` sink to absorb the write. That was built on a wrong
 * premise: MapLibre 5.x does NOT drop `_canvas` in `remove()` — it detaches
 * the canvas container from the DOM and leaves the element in place, so
 * `getCanvas()` keeps returning a real element for the life of the object.
 *
 * THE THIRD CRASH (Sentry, Sep 2, iOS 18.7, release 168d14c2):
 * `TypeError: t.getBoundingClientRect is not a function` from inside
 * maplibre's own `HandlerManager.handleWindowEvent → mousePos → getScale`.
 * MapLibre's window-level mousemove/mouseup listener calls
 * `this._map.getCanvas()` — which is OUR patched method, because the patch
 * lives on the instance — and then does geometry on the result. During a
 * WebGL context loss `map.style` is null but the handlers are still bound
 * (see mapAlive), so `mapAlive()` said "dead", the patch handed maplibre the
 * sink, and maplibre called `.getBoundingClientRect()` on a plain object.
 * The guard didn't remove a crash there; it manufactured one.
 *
 * So the dead path now returns the REAL canvas whenever the map still has
 * one, and falls back to the sink only if `_canvas` has actually gone
 * (never, in 5.24 — kept as a belt for a future version that does drop it).
 * A real element absorbs a `.style.cursor` write just as well as the sink
 * did, and it also answers geometry questions truthfully.
 *
 * Moving or resizing a destroyed map is meaningless, so these no-op.
 *
 * NOT WRAPPED, deliberately: getZoom, getBearing, unproject, getContainer.
 * Those return values a caller does arithmetic on (`Math.max(map.getZoom(),
 * 13)`), and inventing a number to avoid a crash would put a fabricated
 * measurement into the app — which is the one thing this codebase does not
 * do. They read the transform, which survives remove(); if one ever does
 * throw, the honest fix is at the call site.
 */
const CAMERA_METHODS = [
  "easeTo", "flyTo", "jumpTo", "fitBounds", "panTo", "zoomTo",
  "resize", "stop", "setStyle", "setCenter", "setZoom", "setBearing", "setPitch",
] as const;
const MUTATE_METHODS = [
  "addLayer", "addSource", "removeLayer", "removeSource", "moveLayer",
  "setPaintProperty", "setLayoutProperty", "setFilter", "setLayerZoomRange",
  "setTerrain", "setFog",
] as const;

/**
 * Make one map instance survive its own teardown.
 *
 * Call immediately after `new mapboxgl.Map(...)` / `new maplibregl.Map(...)`.
 * Idempotent — calling twice on the same map does nothing the second time.
 */
export function hardenMapAgainstTeardown(map: unknown, label: string): void {
  if (!map) return;
  const m = map as Record<string, unknown> & { __pleasantHardened?: boolean };
  if (m.__pleasantHardened) return;
  m.__pleasantHardened = true;

  let confessed = false;
  const confess = (method: string) => {
    if (confessed) return;
    confessed = true;
    // eslint-disable-next-line no-console
    console.log(
      `[map-teardown] ${label}: ${method}() called after the map was destroyed (or lost its WebGL context) — ` +
        `returned empty instead of throwing. This is the crash that used to ` +
        `white-screen the app; if you see it often, something is still holding ` +
        `a reference it should have dropped.`,
    );
  };

  const wrap = (method: string, dead: (self: unknown) => unknown) => {
    const original = m[method];
    if (typeof original !== "function") return; // method absent in this version
    const fn = original as (...a: unknown[]) => unknown;
    m[method] = function patched(this: unknown, ...args: unknown[]) {
      if (!mapAlive(m)) {
        confess(method);
        return dead(this);
      }
      return fn.apply(this, args);
    };
  };

  for (const name of READ_METHODS) wrap(name, () => undefined);
  for (const name of QUERY_METHODS) wrap(name, () => []);
  for (const name of CAMERA_METHODS) wrap(name, (self) => self);
  // getCanvas: hand back the real canvas if the map still has one — MapLibre's
  // own event handlers call this and do geometry on the answer (see the
  // "THIRD CRASH" note above). Only if the element is truly gone do we fall
  // back to a bare `style` bag that absorbs a `.style.cursor` write; that is a
  // sink, not a claim about the world.
  wrap("getCanvas", (self) => {
    const canvas = (self as { _canvas?: unknown } | null)?._canvas;
    return canvas && typeof (canvas as { getBoundingClientRect?: unknown }).getBoundingClientRect === "function"
      ? canvas
      : { style: {} };
  });
  // Mutators are chainable in the MapLibre API (`map.addLayer(...).addSource(...)`),
  // so a dead one must still hand back something chainable rather than undefined.
  for (const name of MUTATE_METHODS) wrap(name, (self) => self);
  // A destroyed map's style is, unambiguously, not loaded.
  wrap("isStyleLoaded", () => false);
  wrap("loaded", () => false);

  // DIRECT WEBGL CONTEXT LOSS REPORT (PLE-6). See the file header: everything
  // above only ever catches context loss indirectly, after something else
  // calls a guarded method on a map that's already gone quiet. This reports
  // the loss itself, the instant it happens, straight to Sentry — not a fix,
  // pure observation (rule 17: a decider must confess; this one had nothing
  // to confess with until now).
  //
  // No preventDefault(): MapLibre installs its own context-loss handler and
  // decides on its own whether to attempt recovery. This listener only
  // watches; it must never change what the map does.
  //
  // `getCanvas` is read through `m` (not the raw constructor arg), so this
  // runs through whatever `wrap` above already produced — but the map was
  // just constructed and is alive, so that only ever calls through to the
  // real method (see `wrap`'s `mapAlive` check).
  try {
    const getCanvas = m.getCanvas as (() => unknown) | undefined;
    const canvas = typeof getCanvas === "function" ? getCanvas.call(m) : undefined;
    const target = canvas as
      | { addEventListener?: (type: string, cb: () => void) => void }
      | undefined;
    if (target && typeof target.addEventListener === "function") {
      target.addEventListener("webglcontextlost", () => {
        console.log(`[map-teardown] ${label}: webglcontextlost — reporting to Sentry`);
        captureError(new Error(`webglcontextlost: ${label}`), { map: label });
      });
      target.addEventListener("webglcontextrestored", () => {
        console.log(`[map-teardown] ${label}: webglcontextrestored`);
      });
    }
  } catch {
    /* No canvas yet on this map/version — nothing to attach to. Construction
       itself must never fail because of a diagnostic. */
  }
}
